import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  input,
  OnInit,
  output,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NgControl, ReactiveFormsModule } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TooltipDirective } from '../../ui/tooltip/tooltip.directive';
import { TextTickerDirective } from '../../../directives/text-ticker.directive';
import { faInfoCircle } from '@fortawesome/pro-duotone-svg-icons/faInfoCircle';
import { faCheck } from '@fortawesome/pro-duotone-svg-icons/faCheck';
import { faXmark } from '@fortawesome/pro-duotone-svg-icons/faXmark';
import { faSpinnerThird } from '@fortawesome/pro-duotone-svg-icons/faSpinnerThird';
import { TextFieldAction, TextFieldActionFeedback } from './text-field.types';

type TrailingState = 'idle' | 'loading' | 'success' | 'error';

// How long an inline action's success/error icon lingers before reverting to
// its normal icon. Shorter than the trailing button's reason-ticker cooldown
// since an inline action only flashes an icon, not a message.
const INLINE_ACTION_FEEDBACK_MS = 1500;

let uniqueIdCounter = 0;

@Component({
  selector: 'andor-text-field',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FontAwesomeModule,
    TooltipDirective,
    TextTickerDirective,
  ],
  templateUrl: './text-field.html',
  styleUrl: './text-field.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TextField implements ControlValueAccessor, OnInit {
  label = input<string>('');
  placeholder = input<string>('');
  hint = input<string>('');
  labelTooltip = input<string>('');

  type = input<'text' | 'password' | 'email' | 'number'>('text');
  autocomplete = input<string>('off');
  inputId = input<string>(`andor-text-field-${++uniqueIdCounter}`);
  readonly = input<boolean>(false);

  inlineActions = input<TextFieldAction[]>([]);
  inlineActionsPosition = input<'start' | 'end'>('end');
  trailingButton = input<TextFieldAction | null>(null);
  trailingFeedbackCooldown = input<number>(5000);

  errorMessages = input<Record<string, string>>({});
  errorMessage = input<string>('');
  showErrorOn = input<'touched' | 'dirty' | 'always'>('touched');

  enterPressed = output<string>();

  @ViewChild('inputEl') private inputRef?: ElementRef<HTMLInputElement>;

  protected readonly faInfoCircle = faInfoCircle;
  protected readonly faCheck = faCheck;
  protected readonly faXmark = faXmark;
  protected readonly faSpinnerThird = faSpinnerThird;

  protected readonly trailingState = signal<TrailingState>('idle');
  protected readonly trailingReason = signal<string>('');
  private trailingResetTimeout: ReturnType<typeof setTimeout> | null = null;
  // Per-inline-action feedback state, keyed by the action's index in the
  // `inlineActions` array. Only async (Promise-returning) actions use it.
  private readonly inlineActionStates = signal<Record<number, TrailingState>>({});
  private inlineActionResetTimeouts: Record<number, ReturnType<typeof setTimeout>> = {};
  protected readonly value = signal<string>('');
  protected readonly disabled = signal<boolean>(false);
  private readonly touched = signal<boolean>(false);
  private readonly dirty = signal<boolean>(false);

  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  protected readonly ngControl = inject(NgControl, { optional: true, self: true });
  private readonly destroyRef = inject(DestroyRef);
  // Tick signal bumped whenever the bound control's status changes. The
  // NgControl's underlying `control` is only set by the forms directive
  // after this component's constructor runs, so we subscribe in ngOnInit
  // instead of wiring `toSignal` eagerly — otherwise the signal falls back
  // to EMPTY and the error computed silently caches a stale value.
  private readonly statusTick = signal(0);

  constructor() {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  ngOnInit(): void {
    const control = this.ngControl?.control;
    if (!control) return;
    control.statusChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.statusTick.update((v) => v + 1);
    });
  }

  protected readonly feedbackMessage = computed(() => {
    const state = this.trailingState();
    if (state !== 'success' && state !== 'error') return '';
    return this.trailingReason();
  });

  protected readonly currentError = computed(() => {
    this.statusTick();
    this.touched();
    this.dirty();

    if (this.feedbackMessage()) return '';

    const show = this.showErrorOn();
    const errors = this.ngControl?.errors ?? null;
    if (!errors) return '';

    const isTouched = this.ngControl?.touched ?? this.touched();
    const isDirty = this.ngControl?.dirty ?? this.dirty();

    if (show === 'touched' && !isTouched) return '';
    if (show === 'dirty' && !isDirty) return '';

    const messages = this.errorMessages();
    for (const key of Object.keys(errors)) {
      if (messages[key]) return messages[key];
    }
    return this.errorMessage();
  });

  protected readonly hasError = computed(() => this.currentError().length > 0);

  writeValue(value: string | null): void {
    this.value.set(value ?? '');
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  protected onInput(event: Event): void {
    const next = (event.target as HTMLInputElement).value;
    this.value.set(next);
    this.dirty.set(true);
    this.onChange(next);
  }

  protected onBlur(): void {
    this.touched.set(true);
    this.onTouched();
  }

  protected onEnter(): void {
    this.enterPressed.emit(this.value());
    // Enter submits the trailing action — the field's primary affordance —
    // so a value-entry field with a send/confirm button (poke, private
    // message, breakout name, …) is keyboard-submittable without each call
    // site wiring `(enterPressed)` by hand. Routed through `onTrailingClick`
    // so it shares the exact click guards (disabled / in-flight) and the
    // loading / success-error feedback states. No-op when there's no
    // trailing button.
    if (this.trailingButton()) {
      void this.onTrailingClick();
    }
  }

  protected inlineActionState(index: number): TrailingState {
    return this.inlineActionStates()[index] ?? 'idle';
  }

  private setInlineActionState(index: number, state: TrailingState): void {
    this.inlineActionStates.update((states) => ({ ...states, [index]: state }));
  }

  protected async onInlineActionClick(
    action: TextFieldAction,
    event: MouseEvent,
    index: number
  ): Promise<void> {
    if (action.disabled || this.disabled()) return;
    if (this.inlineActionState(index) === 'loading') return;

    const raw = action.click(this.value(), event);
    // Synchronous actions keep their original fire-and-forget behaviour; the
    // loading / success / error affordance only applies to async actions.
    if (!(raw instanceof Promise)) return;

    // Cancel any lingering success/error reset so it can't flip this fresh
    // loading state back to idle mid-flight.
    if (this.inlineActionResetTimeouts[index]) {
      clearTimeout(this.inlineActionResetTimeouts[index]);
      delete this.inlineActionResetTimeouts[index];
    }
    this.setInlineActionState(index, 'loading');
    let errored = false;
    let result: unknown;
    try {
      result = await raw;
    } catch {
      errored = true;
    }

    const feedback = this.normalizeFeedback(result);
    this.setInlineActionState(index, errored || feedback?.error ? 'error' : 'success');

    if (this.inlineActionResetTimeouts[index]) {
      clearTimeout(this.inlineActionResetTimeouts[index]);
    }
    this.inlineActionResetTimeouts[index] = setTimeout(() => {
      this.setInlineActionState(index, 'idle');
      delete this.inlineActionResetTimeouts[index];
    }, INLINE_ACTION_FEEDBACK_MS);
  }

  insertAtCursor(text: string): void {
    if (!text) return;
    const input = this.inputRef?.nativeElement;
    if (!input) return;
    // selectionStart/End persist across blur, so the cursor position from
    // before focus moved to the picker is still readable here.
    const current = input.value;
    const start = input.selectionStart ?? current.length;
    const end = input.selectionEnd ?? current.length;
    const next = current.slice(0, start) + text + current.slice(end);
    input.value = next;
    const caret = start + text.length;
    input.setSelectionRange(caret, caret);
    input.focus();
    this.value.set(next);
    this.dirty.set(true);
    this.onChange(next);
  }

  protected async onTrailingClick(): Promise<void> {
    const action = this.trailingButton();
    if (!action || action.disabled || this.disabled()) return;
    if (this.trailingState() !== 'idle') return;

    const raw = action.click(this.value());
    let result: unknown;
    if (raw instanceof Promise) {
      this.trailingState.set('loading');
      result = await raw.catch((err) => this.toFeedback(err));
    } else {
      result = raw;
    }

    const feedback = this.normalizeFeedback(result);
    if (!feedback) {
      this.trailingState.set('idle');
      return;
    }

    this.trailingState.set(feedback.error ? 'error' : 'success');
    this.trailingReason.set(feedback.reason ?? '');

    if (this.trailingResetTimeout) clearTimeout(this.trailingResetTimeout);
    this.trailingResetTimeout = setTimeout(() => {
      this.trailingState.set('idle');
      this.trailingReason.set('');
      this.trailingResetTimeout = null;
    }, this.trailingFeedbackCooldown());
  }

  private normalizeFeedback(value: unknown): TextFieldActionFeedback | null {
    if (!value || typeof value !== 'object') return null;
    const obj = value as TextFieldActionFeedback;
    if (obj.success === undefined && obj.error === undefined && !obj.reason) return null;
    return obj;
  }

  private toFeedback(err: unknown): TextFieldActionFeedback {
    return {
      error: true,
      reason: err instanceof Error ? err.message : typeof err === 'string' ? err : undefined,
    };
  }
}
